import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Download, Share2, Copy, Check, Twitter, Linkedin, Facebook, Mail, MessageCircle, Award, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificateCard = ({ cert, language, isPublic = false }) => {
  const levelColors = {
    beginner: '#10B981',
    intermediate: '#F59E0B',
    advanced: '#EF4444'
  };

  const levelLabels = {
    fr: { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' },
    en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' }
  };

  const formattedDate = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  }) : '';

  return (
    <div
      className="relative bg-white border-2 border-blue-100 rounded-2xl overflow-hidden"
      style={{ aspectRatio: '1.414/1', maxWidth: '800px', margin: '0 auto' }}
      data-testid="certificate-card"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234F46E5' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />

      {/* Top decorative bar */}
      <div className="h-2 bg-gradient-to-r from-blue-600 via-orange-500 to-blue-600" />

      <div className="relative p-8 md:p-12 flex flex-col items-center justify-between h-full">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-bold text-blue-600 tracking-tight" style={{ fontFamily: 'Poppins' }}>
              IA Academy
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }}>
            {language === 'fr' ? 'CERTIFICAT DE FORMATION' : 'TRAINING CERTIFICATE'}
          </h1>
          <div className="w-24 h-0.5 bg-blue-600 mx-auto mt-3" />
        </div>

        {/* Body */}
        <div className="text-center flex-1 flex flex-col justify-center">
          <p className="text-sm text-slate-500 mb-1">
            {language === 'fr' ? 'Décerné à' : 'Awarded to'}
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-blue-700 mb-4" style={{ fontFamily: 'Poppins' }} data-testid="cert-user-name">
            {cert.user_name}
          </h2>
          <p className="text-sm text-slate-500 mb-2">
            {language === 'fr' ? 'pour avoir complété avec succès' : 'for successfully completing'}
          </p>
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins' }} data-testid="cert-program-title">
            {language === 'fr' ? cert.program_title_fr : cert.program_title_en}
          </h3>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Poppins' }}>{cert.program_duration}</p>
              <p className="text-xs text-slate-400">{language === 'fr' ? 'Durée' : 'Duration'}</p>
            </div>
            <div className="w-px h-10 bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Poppins' }}>{cert.avg_score}%</p>
              <p className="text-xs text-slate-400">Score</p>
            </div>
            <div className="w-px h-10 bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600" style={{ fontFamily: 'Poppins' }}>{cert.modules_completed}/{cert.total_modules}</p>
              <p className="text-xs text-slate-400">Modules</p>
            </div>
          </div>

          <Badge
            className="mx-auto"
            style={{ backgroundColor: `${levelColors[cert.program_level]}20`, color: levelColors[cert.program_level], borderColor: levelColors[cert.program_level] }}
          >
            {language === 'fr' ? `Niveau ${levelLabels.fr[cert.program_level]}` : `${levelLabels.en[cert.program_level]} Level`}
          </Badge>
        </div>

        {/* Footer */}
        <div className="w-full flex items-end justify-between mt-4 pt-4 border-t border-slate-100">
          <div className="text-left">
            <p className="text-xs text-slate-400">{language === 'fr' ? 'Date' : 'Date'}</p>
            <p className="text-sm font-medium text-slate-600">{formattedDate}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-400">{language === 'fr' ? 'Code de vérification' : 'Verification Code'}</p>
            <p className="text-sm font-mono font-medium text-blue-600" data-testid="cert-verification-code">{cert.verification_code}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">ID</p>
            <p className="text-sm font-mono text-slate-500">{cert.cert_id?.slice(0, 12)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShareDialog = ({ cert, language, t }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/certificate/${cert.cert_id}`;
  const shareText = language === 'fr'
    ? `J'ai obtenu mon certificat "${cert.program_title_fr}" sur IA Academy ! Score: ${cert.avg_score}%`
    : `I earned my "${cert.program_title_en}" certificate on IA Academy! Score: ${cert.avg_score}%`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socialLinks = [
    {
      name: 'Twitter / X',
      icon: Twitter,
      color: 'bg-slate-900 hover:bg-slate-800',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'bg-blue-700 hover:bg-blue-800',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-600 hover:bg-green-700',
      url: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'bg-slate-600 hover:bg-slate-700',
      url: `mailto:?subject=${encodeURIComponent(language === 'fr' ? 'Mon certificat IA Academy' : 'My IA Academy Certificate')}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
    }
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="share-cert-btn">
          <Share2 className="w-4 h-4" /> {t('cert_share')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Poppins' }}>
            <Share2 className="w-5 h-5 text-blue-600" />
            {t('cert_share_title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Copy Link */}
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 truncate">
              {shareUrl}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className={`shrink-0 gap-1.5 ${copied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t('cert_copied') : t('cert_copy_link')}
            </Button>
          </div>

          {/* Social Buttons */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">{t('cert_share_on')}</p>
            <div className="grid grid-cols-2 gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-medium transition-colors ${social.color}`}
                  data-testid={`share-${social.name.toLowerCase().replace(/\s|\/|\s/g, '-')}`}
                >
                  <social.icon className="w-4 h-4" />
                  {social.name}
                </a>
              ))}
            </div>
          </div>

          {/* Preview text */}
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-xs text-slate-400 mb-1">{language === 'fr' ? 'Aperçu du message' : 'Message preview'}</p>
            <p className="text-sm text-slate-600">{shareText}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CertificatePage() {
  const { certId } = useParams();
  const { language, t } = useLanguage();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const certRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (certId) {
          const res = await axios.get(`${API}/certificates/${certId}`);
          setCertificate(res.data);
        } else if (user) {
          const res = await axios.get(`${API}/certificates`, { headers: getAuthHeaders(), withCredentials: true });
          setCertificates(res.data);
        }
      } catch (err) {
        console.error('Certificate fetch error:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [certId, user, getAuthHeaders]);

  const handleDownload = async () => {
    if (!certRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(certRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `AI_Bootcamp_Certificate_${certificate?.verification_code || 'cert'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Single certificate view
  if (certId && certificate) {
    return (
      <div className="min-h-screen bg-[#E8ECF1]" data-testid="certificate-view-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button variant="ghost" className="mb-6 text-slate-600" onClick={() => navigate('/certificates')} data-testid="back-to-certs">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>

          <div ref={certRef}>
            <CertificateCard cert={certificate} language={language} />
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 active:scale-95 transition-transform"
              onClick={handleDownload}
              data-testid="download-cert-btn"
            >
              <Download className="w-4 h-4" /> {t('cert_download')}
            </Button>
            <ShareDialog cert={certificate} language={language} t={t} />
          </div>
        </div>
      </div>
    );
  }

  // Certificates list view
  return (
    <div className="min-h-screen bg-[#E8ECF1]" data-testid="certificates-list-page">
      <SEO
        title={language === 'fr' ? 'Mes Certificats' : 'My Certificates'}
        description={language === 'fr'
          ? "Consultez et partagez vos certificats IA Academy. Chaque certificat atteste de vos compétences en intelligence artificielle."
          : "View and share your IA Academy certificates. Each certificate validates your AI skills."}
        path="/certificates"
        noIndex
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-10 h-10 rounded-lg" data-testid="certificates-logo" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Poppins' }} data-testid="certificates-title">
              {t('cert_title')}
            </h1>
          </div>
          <p className="text-slate-500 mt-2">{t('cert_subtitle')}</p>
        </div>

        {certificates.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="no-certificates">
            <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">{t('cert_no_certs')}</p>
            <Button className="bg-blue-600 text-white" onClick={() => navigate('/programs')} data-testid="go-to-programs-btn">
              {t('hero_explore')}
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {certificates.map((cert) => (
              <div
                key={cert.cert_id}
                className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate(`/certificate/${cert.cert_id}`)}
                data-testid={`cert-item-${cert.cert_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-blue-600" />
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Poppins' }}>
                  {language === 'fr' ? cert.program_title_fr : cert.program_title_en}
                </h3>
                <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                  <span>{cert.program_duration}</span>
                  <span>Score: {cert.avg_score}%</span>
                </div>
                <p className="text-xs text-slate-400">
                  {new Date(cert.issued_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="outline" className="text-xs gap-1" onClick={(e) => { e.stopPropagation(); navigate(`/certificate/${cert.cert_id}`); }} data-testid={`view-cert-${cert.cert_id}`}>
                    {t('cert_view')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
